import { useEffect, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Trash2 } from 'lucide-react';
import {
  Button, Card, CardBody, 
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import {
  useCustomerServiceInterestings,
  useProductsInGroup,
  useProductDetail,
} from '@/api/products';
import { errorFor } from './form-controls';
import { ProductTemplateDispatcher } from './product-templates/product-template-dispatcher';
import { isStatementable } from './product-templates/statementable-templates';
import { productConfigHint } from './product-config-summary';
import { formatContractAmount } from './format-contract-amount';
import type { ContractFormValues } from './contract-form';
import { DateInput } from '@/components/date-input';
import { NumericInput } from '@/components/numeric-input';

/** A bound date input for a `products.{i}.{field}` service-time path. */
function DateCell({ index, field, label }: { index: number; field: string; label: string }) {
  const { control } = useFormContext<ContractFormValues>();
  const name = `products.${index}.${field}` as const;
  return (
    <Controller
      control={control}
      name={name as never}
      render={({ field: f }) => (
        <Field label={label}>
          {(p) => (
            <DateInput
              {...p}
              value={(f.value as string) ?? ''}
              onChange={f.onChange}
              onBlur={f.onBlur}
              name={f.name}
              ref={f.ref}
            />
          )}
        </Field>
      )}
    />
  );
}

/**
 * A single order product line. Cascades customer service-interest GROUP →
 * PRODUCT (auto-select first non-hidden) → detail sync (price model, register,
 * template, group code). Service-time dates default to the contract term and
 * are editable; the per-type deep config (service_configoptions / domain
 * metadata) is a later phase and left empty.
 */
export function ProductLine({
  index,
  customerId,
  onRemove,
  canRemove,
}: {
  index: number;
  customerId: number;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const { control, formState, setValue } = useFormContext<ContractFormValues>();
  const base = `products.${index}` as const;

  const groupName = useWatch({ control, name: `${base}.product_group_name` });
  const productId = useWatch({ control, name: `${base}.product_id` });
  const templateName = useWatch({ control, name: `${base}.template_name` });
  const productName = useWatch({ control, name: `${base}.product_name` });
  const configOptions = useWatch({ control, name: `${base}.service_configoptions` });
  const registerPrice = useWatch({ control, name: `${base}.service_price_register` });
  const currency = useWatch({ control, name: 'currency_code' });

  const { data: groups = [] } = useCustomerServiceInterestings(customerId);
  const { data: products = [] } = useProductsInGroup(groupName);
  const { data: detail } = useProductDetail(productId);

  const visibleProducts = products.filter((p) => !p.is_hidden);
  const groupErr = errorFor(formState.errors, `${base}.product_group_name`);
  const productErr = errorFor(formState.errors, `${base}.product_id`);

  // Auto-select the first non-hidden product when the group's product set loads
  // and nothing valid is selected yet (legacy `hasAutoSelectedProduct`).
  const autoRef = useRef<string>('');
  useEffect(() => {
    if (!groupName || visibleProducts.length === 0) return;
    const stillValid = visibleProducts.some((p) => p.product_id === productId);
    if (stillValid || autoRef.current === groupName) return;
    autoRef.current = groupName;
    setValue(`${base}.product_id`, visibleProducts[0].product_id);
  }, [groupName, visibleProducts, productId, base, setValue]);

  // Sync product detail → form (price model, register, name, group code, template).
  // On a real product switch, drop the previous product's deep config first so a
  // per-type template re-seeds cleanly (its seed effect skips non-empty options).
  const syncedProductRef = useRef<number>(0);
  useEffect(() => {
    if (!detail || detail.product_id !== productId) return;
    if (syncedProductRef.current !== productId) {
      if (syncedProductRef.current !== 0) {
        setValue(`${base}.service_configoptions`, []);
        setValue(`${base}.service_domain_metadata`, {});
      }
      syncedProductRef.current = productId;
    }
    setValue(`${base}.product_name`, detail.product_name ?? '');
    setValue(`${base}.product_group_code`, detail.product_group_code ?? '');
    const nextTemplate = detail.template_name ?? '';
    setValue(`${base}.template_name`, nextTemplate);
    // The catalog carries the pricing model under `product_price_model` (legacy
    // Base: `service_price_model = product.product_price_model || 'package'`).
    // `pricing_models` is a NOT-NULL enum, so it must never be empty. When the
    // catalog omits it, derive: statement/usage products (CDN, Transcoding) use
    // 'bundle' so the backend keeps each config's own prepaid flag; everything
    // else uses 'package' (note: 'package' forces prepaid=true on all configs).
    const catalogModel =
      (detail as { product_price_model?: string }).product_price_model || detail.service_price_model;
    const priceModel = catalogModel || (isStatementable(nextTemplate) ? 'bundle' : 'package');
    setValue(`${base}.service_price_model`, priceModel);
    if (typeof detail.service_price_register === 'number') {
      setValue(`${base}.service_price_register`, detail.service_price_register);
    }
  }, [detail, productId, base, setValue]);

  const onGroupChange = (value: string) => {
    setValue(`${base}.product_group_name`, value);
    // Reset the product + synced fields so the new group auto-selects cleanly.
    setValue(`${base}.product_id`, 0);
    setValue(`${base}.product_name`, '');
    setValue(`${base}.product_group_code`, '');
    setValue(`${base}.template_name`, '');
    setValue(`${base}.service_price_model`, '');
    setValue(`${base}.service_price_register`, 0);
    // Clear the previous type's deep config so it can't leak into the new
    // product's template (per-type seed effects skip when options already exist).
    setValue(`${base}.service_configoptions`, []);
    setValue(`${base}.service_domain_metadata`, {});
    autoRef.current = '';
  };

  // Scannable header: once group+product are chosen, title reads "Group — Product"
  // (fallback "Product N" before selection). A short compact glance sits beside
  // it ("Predefined · 1 VM, 1 LB, 1 add-on") — the full item-level quotation
  // summary lives in the Total section, not here. When nothing is derivable yet,
  // fall back to the line amount so the header still says something concrete.
  const hasSelection = Boolean(groupName && productName);
  const title = hasSelection ? `${groupName} — ${productName}` : `Product ${index + 1}`;
  const glance = productConfigHint(templateName, configOptions, { compact: true });
  const headerHint = hasSelection
    ? glance ?? formatContractAmount(Number(registerPrice) || 0, currency || 'VND')
    : null;

  return (
    <Card>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--vn-space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--vn-space-3)', minWidth: 0 }}>
            <strong
              style={{
                fontSize: 'var(--vn-font-size-sm)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </strong>
            {headerHint && (
              <span
                style={{
                  fontSize: 'var(--vn-font-size-xs)',
                  color: 'var(--vn-fg-muted)',
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {headerHint}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            startIcon={<Trash2 size={15} />}
            disabled={!canRemove}
            onClick={onRemove}
          >
            Remove
          </Button>
        </div>

        <div
          style={{
            display: 'grid',
            gap: 'var(--vn-space-4)',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          }}
        >
          <Field label="Product group" required error={groupErr}>
            {(p) => (
              <Select value={groupName || ''} onValueChange={onGroupChange}>
                <SelectTrigger {...p} invalid={!!groupErr}>
                  <SelectValue placeholder="Select a group" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((g) => (
                    <SelectItem key={g.product_group_name} value={g.product_group_name}>
                      {g.product_group_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field label="Product" required error={productErr}>
            {(p) => (
              <Select
                value={productId > 0 ? String(productId) : ''}
                onValueChange={(v) => setValue(`${base}.product_id`, Number(v))}
                disabled={!groupName}
              >
                <SelectTrigger {...p} invalid={!!productErr} disabled={!groupName}>
                  <SelectValue placeholder={groupName ? 'Select a product' : 'Select a group first'} />
                </SelectTrigger>
                <SelectContent>
                  {visibleProducts.map((prod) => (
                    <SelectItem key={prod.product_id} value={String(prod.product_id)}>
                      {prod.product_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Controller
            control={control}
            name={`${base}.service_price_register`}
            render={({ field: f }) => (
              <Field label="Register price">
                {(p) => (
                  <NumericInput
                    {...p}
                    min={0}
                    value={f.value == null ? '' : String(f.value)}
                    onChange={(e) => f.onChange(Number(e.target.value))}
                    onBlur={f.onBlur}
                    name={f.name}
                    ref={f.ref}
                  />
                )}
              </Field>
            )}
          />
        </div>

        <div
          style={{
            display: 'grid',
            gap: 'var(--vn-space-4)',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          }}
        >
          <DateCell index={index} field="first_startdate" label="First period start" />
          <DateCell index={index} field="first_enddate" label="First period end" />
          <DateCell index={index} field="continue_startdate" label="Renewal start" />
          <DateCell index={index} field="continue_enddate" label="Renewal end" />
        </div>

        {productId > 0 && (templateName || groupName) ? (
          <ProductTemplateDispatcher
            index={index}
            templateName={templateName ?? ''}
            productGroupName={groupName ?? ''}
            detail={detail}
          />
        ) : null}
      </CardBody>
    </Card>
  );
}
